import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Avatar,
  InputAdornment,
} from '@mui/material';
import {
  Email as EmailIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { styled } from '@mui/system';
import api from '../config/api';
import PublicEvaluationInstructions from './PublicEvaluationInstructions';
import PublicEvaluationQuestions from './PublicEvaluationQuestions';
import { useTranslation } from 'react-i18next';

// Componentes estilizados
const StyledContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: '#f5f5f5',
  padding: theme.spacing(2),
}));

const StyledCard = styled(Card)(({ theme }) => ({
  maxWidth: 500,
  width: '100%',
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
}));

const PublicEvaluationLayout = () => { // Agregar props para i18n
  const { responseId, language: languageParam } = useParams();

  // Estados principales
  const [currentStep, setCurrentStep] = useState('access'); // 'access', 'instructions', 'evaluation', 'completed', 'expired', 'error'
  const [applicantEmail, setApplicantEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [evaluationData, setEvaluationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [language, setLanguage] = useState("es");

  const { t, i18n } = useTranslation();

  // Setear language desde params o usar "es" por defecto
  useEffect(() => {
    const lang = languageParam || 'es';
    if (lang === 'es' || lang === 'en') {
      i18n.changeLanguage(lang);
      setLanguage(lang);
    } else {
      i18n.changeLanguage('es'); // fallback si el parámetro no es válido
    }
  }, [languageParam, i18n]);

  // Función para iniciar la evaluación
  const handleStartEvaluation = async () => {
    try {
      setLoading(true);

      // Verificar el estado antes de intentar iniciar
      if (evaluationData?.overallStatus === 'completed') {
        setError(t('publicEvaluations.evaluationAlreadyCompleted'));
        setCurrentStep('completed');
        return;
      }

      if (evaluationData?.overallStatus === 'expired') {
        setError(t('publicEvaluations.evaluationExpiredNotAvailable'));
        setCurrentStep('expired');
        return;
      }

      if (evaluationData?.overallStatus === 'started') {
        // Si ya está iniciada, ir directamente a las preguntas
        setCurrentStep('evaluation');
        return;
      }

      // Llamar al endpoint para iniciar la evaluación
      const response = await api.put(
        `/public/evaluation-response/${responseId}/start`,
        { applicantEmail: applicantEmail }
      );

      if (response.data.errorCode === 0 || response.data.resultCode === 200) {
        // Actualizar los datos de evaluación con la respuesta
        setEvaluationData(response.data.response?.data || response.data.data);
        setCurrentStep('evaluation');
      } else {
        const errorMessage = response.data.errorMessage ||
          response.data.response?.message ||
          t('publicEvaluations.errorStartingEvaluation');
        setError(errorMessage);
        setCurrentStep('error');
      }
    } catch (err) {
      console.error('Error starting evaluation:', err);

      if (err.response?.status === 400) {
        const errorMessage = err.response.data?.errorMessage ||
          err.response.data?.response?.message ||
          err.response.data?.message ||
          t('publicEvaluations.errorStartingEvaluation');
        setError(errorMessage);

        // Si el error indica que ya está completada o expirada
        if (errorMessage.toLowerCase().includes('completada')) {
          setCurrentStep('completed');
        } else if (errorMessage.toLowerCase().includes('expirada')) {
          setCurrentStep('expired');
        } else {
          setCurrentStep('error');
        }
      } else if (err.response?.data?.errorMessage) {
        setError(err.response.data.errorMessage);
        setCurrentStep('error');
      } else {
        setError(t('publicEvaluations.errorStartingEvaluation'));
        setCurrentStep('error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Función para cargar datos de la evaluación
  const loadEvaluationData = async () => {
    try {
      const response = await api.get(
        `/public/evaluation-response/${responseId}`,
        {
          params: { applicantEmail: applicantEmail }
        }
      );

      if (response.data.errorCode === 0 || response.data.resultCode === 200) {
        const data = response.data.response?.data || response.data.data;
        setEvaluationData(data);

        // Verificar el estado de la evaluación y dirigir apropiadamente
        const status = data.overallStatus;

        switch (status) {
          case 'completed':
            setCurrentStep('completed');
            break;
          case 'expired':
            setError(t('publicEvaluations.evaluationExpiredNotAvailable'));
            setCurrentStep('expired');
            break;
          case 'started':
            // Si ya está iniciada, ir directamente a las preguntas
            setCurrentStep('evaluation');
            break;
          default:
            // Estados 'pending', 'opened', etc. - mostrar instrucciones
            setCurrentStep('instructions');
            break;
        }
      } else {
        const errorMessage = response.data.errorMessage ||
          response.data.response?.message ||
          t('publicEvaluations.errorLoadingEvaluation');
        setError(errorMessage);
        setCurrentStep('error');
      }
    } catch (err) {
      console.error('Error loading evaluation:', err);

      if (err.response?.status === 400) {
        const errorMessage = err.response.data?.errorMessage ||
          err.response.data?.response?.message ||
          err.response.data?.message ||
          t('publicEvaluations.errorLoadingEvaluation');
        setError(errorMessage);
      } else if (err.response?.data?.errorMessage) {
        setError(err.response.data.errorMessage);
      } else if (err.response?.data?.response?.message) {
        setError(err.response.data.response.message);
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError(t('publicEvaluations.errorLoadingEvaluation'));
      }
      setCurrentStep('error');
    }
  };

  // Función para completar la evaluación
  const handleCompleteEvaluation = (expired) => {
    if (expired) {
      setCurrentStep('expired');
    } else {
      setCurrentStep('completed');
    }
  };

  // Función para validar email
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Función para manejar cambio en el email
  const handleEmailChange = (event) => {
    const value = event.target.value;
    setApplicantEmail(value);

    // Validar email si tiene contenido
    if (value.trim()) {
      if (isValidEmail(value)) {
        setEmailError('');
      } else {
        setEmailError(t('publicEvaluations.invalidEmail'));
      }
    } else {
      setEmailError('');
    }
  };

  // Función para validar acceso con email
  const handleAccessValidation = async (e) => {
    e.preventDefault();

    if (!applicantEmail) {
      setEmailError(t('publicEvaluations.pleaseEnterEmail'));
      return;
    }

    if (!isValidEmail(applicantEmail)) {
      setEmailError(t('publicEvaluations.invalidEmail'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Llamar al endpoint de validación
      const response = await api.get(
        `/public/evaluation-response/${responseId}/validate`,
        {
          params: { applicantEmail: applicantEmail }
        }
      );

      // Verificar si la respuesta es exitosa
      if (response.data.errorCode === 0 || response.data.resultCode === 200) {
        // Cargar datos de la evaluación
        await loadEvaluationData();
      } else {
        // Manejar diferentes tipos de error
        const errorMessage = response.data.errorMessage ||
          response.data.response?.message ||
          t('publicEvaluations.noAccessToEvaluation');
        setError(errorMessage);
        setCurrentStep('error');
      }
    } catch (err) {
      console.error('Error validating access:', err);

      if (err.response?.status === 404) {
        setError(t('publicEvaluations.evaluationNotFound'));
      } else if (err.response?.status === 403) {
        setError(t('publicEvaluations.noAccessWithProvidedEmail'));
      } else if (err.response?.status === 400) {
        // Casos específicos de bad request como evaluación expirada
        const errorMessage = err.response.data?.errorMessage ||
          err.response.data?.response?.message ||
          err.response.data?.message ||
          t('publicEvaluations.errorValidatingAccess');
        setError(errorMessage);
      } else if (err.response?.data?.errorMessage) {
        setError(err.response.data.errorMessage);
      } else if (err.response?.data?.response?.message) {
        setError(err.response.data.response.message);
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError(t('publicEvaluations.errorValidatingAccessTryAgain'));
      }
      setCurrentStep('error');
    } finally {
      setLoading(false);
    }
  };

  // Renderizar formulario de acceso
  const renderAccessForm = () => (
    <StyledContainer>
      <StyledCard>
        <CardContent>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Avatar sx={{
              mx: 'auto',
              mb: 2,
              bgcolor: 'primary.main',
              width: 64,
              height: 64
            }}>
              <SecurityIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Typography variant="h5" component="h1" gutterBottom>
              {t('publicEvaluations.evaluationAccess')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('publicEvaluations.enterEmailToAccess')}
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleAccessValidation}>
            <TextField
              fullWidth
              label={t('publicEvaluations.email')}
              placeholder={t('publicEvaluations.emailPlaceholder')}
              type="email"
              value={applicantEmail}
              onChange={handleEmailChange}
              error={!!emailError}
              helperText={emailError || t('publicEvaluations.enterRegisteredEmail')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
              disabled={loading}
            />

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || !!emailError || !applicantEmail}
              sx={{
                mt: 2,
                py: 1.5,
                fontSize: '1.1rem'
              }}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  {t('publicEvaluations.validating')}
                </>
              ) : (
                t('publicEvaluations.accessEvaluation')
              )}
            </Button>
          </Box>
        </CardContent>
      </StyledCard>
    </StyledContainer>
  );

  // Renderizar instrucciones
  const renderInstructions = () => (
    <PublicEvaluationInstructions
      evaluationData={evaluationData}
      onStartEvaluation={handleStartEvaluation}
      loading={loading}
    />
  );

  // Renderizar evaluación
  const renderEvaluation = () => (
    <PublicEvaluationQuestions
      responseId={responseId}
      applicantEmail={applicantEmail}
      evaluationData={evaluationData}
      onComplete={handleCompleteEvaluation}
      language={language}
    />
  );

  // Renderizar completado
  const renderCompleted = () => (
    <StyledContainer>
      <StyledCard>
        <CardContent>
          <Box sx={{ textAlign: 'center' }}>
            <Avatar sx={{
              mx: 'auto',
              mb: 2,
              bgcolor: 'success.main',
              width: 64,
              height: 64
            }}>
              <CheckCircleIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Typography variant="h5" component="h1" gutterBottom>
              {t('publicEvaluations.evaluationCompleted')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {t('publicEvaluations.evaluationCompletedDescription')}
            </Typography>
            <Alert severity="success">
              {t('publicEvaluations.thanksForTimeMessage')}
            </Alert>
          </Box>
        </CardContent>
      </StyledCard>
    </StyledContainer>
  );

  // Renderizar expirado
  const renderExpired = () => (
    <StyledContainer>
      <StyledCard>
        <CardContent>
          <Box sx={{ textAlign: 'center' }}>
            <Avatar sx={{
              mx: 'auto',
              mb: 2,
              bgcolor: 'error.main',
              width: 64,
              height: 64
            }}>
              <WarningIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Typography variant="h5" component="h1" gutterBottom color="error.main">
              {t('publicEvaluations.evaluationExpired')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {t('publicEvaluations.timeToCompleteHasExpired')}
            </Typography>
            <Alert severity="error">
              {t('publicEvaluations.contactAdministratorIfError')}
            </Alert>
          </Box>
        </CardContent>
      </StyledCard>
    </StyledContainer>
  );

  // Renderizar error
  const renderError = () => {
    // Determinar el tipo de error para mostrar el alert apropiado
    const isExpiredError = error && error.toLowerCase().includes('expirado');
    const isNotFoundError = error && error.toLowerCase().includes('no encontrada');
    const isAccessDeniedError = error && (error.toLowerCase().includes('no tienes acceso') || error.toLowerCase().includes('acceso denegado'));

    let alertSeverity = 'error';
    let alertTitle = t('publicEvaluations.error');

    if (isExpiredError) {
      alertSeverity = 'warning';
      alertTitle = t('publicEvaluations.evaluationExpired');
    } else if (isNotFoundError) {
      alertSeverity = 'error';
      alertTitle = t('publicEvaluations.evaluationNotFoundTitle');
    } else if (isAccessDeniedError) {
      alertSeverity = 'error';
      alertTitle = t('publicEvaluations.accessDenied');
    }

    return (
      <StyledContainer>
        <StyledCard>
          <CardContent>
            <Box sx={{ textAlign: 'center' }}>
              <Alert severity={alertSeverity} sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {alertTitle}
                </Typography>
                <Typography variant="body2">
                  {error}
                </Typography>
              </Alert>

              {isExpiredError ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {t('publicEvaluations.timeFinishedContactAdmin')}
                </Typography>
              ) : (
                <Button
                  variant="outlined"
                  onClick={() => {
                    setCurrentStep('access');
                    setError(null);
                    setApplicantEmail('');
                    setEmailError('');
                  }}
                >
                  {t('publicEvaluations.tryAgain')}
                </Button>
              )}
            </Box>
          </CardContent>
        </StyledCard>
      </StyledContainer>
    );
  };

  // Render principal según el estado actual
  switch (currentStep) {
    case 'access':
      return renderAccessForm();
    case 'instructions':
      return renderInstructions();
    case 'evaluation':
      return renderEvaluation();
    case 'completed':
      return renderCompleted();
    case 'expired':
      return renderExpired();
    case 'error':
      return renderError();
    default:
      return renderAccessForm();
  }
};

export default PublicEvaluationLayout;